import {z} from 'zod';

import {BaseVoidRequestSchema, BaseVoidResponseSchema} from '@dms/api/shared';

export const PostIntegrationsForXentryLinkApiResponseSchema = BaseVoidResponseSchema;
export type PostIntegrationsForXentryLinkApiResponse = z.infer<
  typeof PostIntegrationsForXentryLinkApiResponseSchema
>;

export const PostIntegrationsForXentryLinkApiArgSchema = z.object({
  serviceCaseId: z.string(),
  body: z
    .object({
      xentryOrderId: z.string(),
    })
    .nullable(),
});

export type PostIntegrationsForXentryLinkApiArg = z.infer<
  typeof PostIntegrationsForXentryLinkApiArgSchema
>;

export const PostIntegrationsForXentryUnlinkApiResponseSchema = BaseVoidResponseSchema;
export type PostIntegrationsForXentryUnlinkApiResponse = z.infer<
  typeof PostIntegrationsForXentryUnlinkApiResponseSchema
>;

export const PostIntegrationsForXentryUnlinkApiArgSchema = z.object({
  serviceCaseId: z.string(),
});

export type PostIntegrationsForXentryUnlinkApiArg = z.infer<
  typeof PostIntegrationsForXentryUnlinkApiArgSchema
>;

export const XentryJobSchema = z.object({
  id: z.string().nullish(),
  createdAt: z.string().nullish(),
  url: z.string().nullish(),
  isCurrentlyLinked: z.boolean().nullish(),
  linkedAt: z.string().nullish(),
});

export type XentryJob = z.infer<typeof XentryJobSchema>;

export const PostAvailableXentryOrdersApiResponseSchema = z
  .object({
    xentryJob: z.array(XentryJobSchema).optional(),
  })
  .nullable();

export type PostAvailableXentryOrdersApiResponse = z.infer<
  typeof PostAvailableXentryOrdersApiResponseSchema
>;

export const PostAvailableXentryOrdersApiArgSchema = z.object({
  serviceCaseId: z.string(),
});

export type PostAvailableXentryOrdersApiArg = z.infer<typeof PostAvailableXentryOrdersApiArgSchema>;

const ServiceCaseIntegrationActionKeySchema = z.union([
  z.literal('xentry-job-sync'),
  z.literal('clara-vehicle-info-view'),
  z.literal('eva-claim-create'),
]);

export type ServiceCaseIntegrationActionKey = z.infer<typeof ServiceCaseIntegrationActionKeySchema>;

const ServiceCaseIntegrationActionSchema = z.object({
  key: ServiceCaseIntegrationActionKeySchema.nullish(),
  style: z.string().nullish(),
  position: z.number().nullish(),
});

export type ServiceCaseIntegrationAction = z.infer<typeof ServiceCaseIntegrationActionSchema>;

const ServiceCaseIntegrationCodeSchema = z.union([
  z.literal('DAIMLER_CLARA_VEHINFO'),
  z.literal('MERCEDES_XENTRY_SYNC'),
  z.literal('MERCEDES_XENTRY_LINK'),
  z.literal('DAIMLER_XENTRY_LINK'),
  z.literal('DAIMLER_XENTRY_SYNC'),
  z.literal('MERCEDES_EVA-CLAIM'),
]);

export type ServiceCaseIntegrationCode = z.infer<typeof ServiceCaseIntegrationCodeSchema>;

const IntegrationSchema = z.object({
  code: z.string().nullish(),
  serviceCode: z.string().nullish(),
  viewDetailsLink: z.string().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
  featureCode: ServiceCaseIntegrationCodeSchema.nullish(),
  externalId: z.string().nullish(),
});

const IntegrationActionSchema = z.object({
  key: z.string().nullish(),
  style: z.string().nullish(),
  position: z.number().nullish(),
});

export type XentryIntegrationAction = z.infer<typeof IntegrationActionSchema>;

const DownloadSchema = z.object({
  link: z.string().url(),
  filename: z.string(),
});

const ServiceCaseIntegrationSchema = z.object({
  integration: IntegrationSchema.nullish(),
  action: z.array(ServiceCaseIntegrationActionSchema.nullable()).optional(),
  downloads: z.array(DownloadSchema).optional(),
  position: z.number().nullish(),
  notification: z
    .object({
      level: z.enum(['info', 'warning', 'error', 'success']),
      content: z.string(),
    })
    .nullish(),
});

export type ServiceCaseIntegration = z.infer<typeof ServiceCaseIntegrationSchema>;

export const GetServiceCaseIntegrationsApiResponseSchema = z
  .object({
    caseIntegration: z.array(ServiceCaseIntegrationSchema).optional(),
  })
  .nullable();

export type GetServiceCaseIntegrationsApiResponse = z.infer<
  typeof GetServiceCaseIntegrationsApiResponseSchema
>;

export const GetServiceCaseIntegrationsApiArgSchema = z.object({
  serviceCaseId: z.string(),
});

export type GetServiceCaseIntegrationsApiArg = z.infer<
  typeof GetServiceCaseIntegrationsApiArgSchema
>;

export const PostServiceOrderAudatexTaskCalculationsApiResponseSchema = BaseVoidResponseSchema;
export type PostServiceOrderAudatexTaskCalculationsApiResponse = z.infer<
  typeof PostServiceOrderAudatexTaskCalculationsApiResponseSchema
>;

const AudatexCalculationSettingsSchema = z.object({
  material: z.boolean(),
  labour: z.boolean(),
  paintWork: z.boolean(),
  paintPreparationWork: z.boolean(),
  paintMaterial: z.boolean(),
  paintConstantMaterial: z.boolean(),
  pcofPart: z.boolean(),
  discountsAndSurcharges: z.boolean(),
  otherCosts: z.boolean(),
});

export type AudatexCalculationSettings = z.infer<typeof AudatexCalculationSettingsSchema>;

export const PostServiceOrderAudatexTaskCalculationsApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  body: z.object({
    caseId: z.string(),
    taskId: z.string(),
    settings: AudatexCalculationSettingsSchema,
  }),
});

export type PostServiceOrderAudatexTaskCalculationsApiArg = z.infer<
  typeof PostServiceOrderAudatexTaskCalculationsApiArgSchema
>;

export const PostServiceOrderMyClaimCalculationsApiResponseSchema = BaseVoidResponseSchema;
export type PostServiceOrderMyClaimCalculationsApiResponse = z.infer<
  typeof PostServiceOrderMyClaimCalculationsApiResponseSchema
>;

const MyClaimCalculationSettingsSchema = z.object({
  material: z.boolean(),
  labour: z.boolean(),
  lacquerWork: z.boolean(),
  lacquerMaterial: z.boolean(),
  lacquerConstant: z.boolean(),
  consumablesSurcharge: z.boolean(),
  dentWork: z.boolean(),
  discountsAndSurcharges: z.boolean(),
  otherCosts: z.boolean(),
});

export type MyClaimCalculationSettings = z.infer<typeof MyClaimCalculationSettingsSchema>;

export const PostServiceOrderMyClaimCalculationsApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  body: z.object({
    myClaimId: z.string(),
    settings: MyClaimCalculationSettingsSchema,
  }),
});

export type PostServiceOrderMyClaimCalculationsApiArg = z.infer<
  typeof PostServiceOrderMyClaimCalculationsApiArgSchema
>;
export const PostServiceCaseIntegrationsXentryOrderManualCreationApiResponseSchema =
  BaseVoidResponseSchema;
export type PostServiceCaseIntegrationsXentryOrderManualCreationApiResponse = z.infer<
  typeof PostServiceCaseIntegrationsXentryOrderManualCreationApiResponseSchema
>;
export const PostServiceCaseIntegrationsXentryOrderManualCreationApiArgSchema = z.object({
  serviceCaseId: z.string(),
});
export type PostServiceCaseIntegrationsXentryOrderManualCreationApiArg = z.infer<
  typeof PostServiceCaseIntegrationsXentryOrderManualCreationApiArgSchema
>;

const ServiceCaseIntegrationsXentryOrderSynchronizationGroupSchema = z.object({
  type: z.string().nullable(),
  name: z.string().nullable().optional(),
  selectedSource: z.string().nullable().optional(),
  isVisibleForUser: z.boolean().nullable().optional(),
  data: z
    .array(
      z
        .object({
          labelName: z.string().nullable(),
          valueOmnetic: z.string().nullable().optional(),
          valueXentry: z.string().nullable().optional(),
          valueType: z.string().nullable().optional(),
          unit: z.string().nullable().optional(),
        })
        .nullable()
    )
    .optional(),
  groups: z
    .array(
      z
        .object({
          type: z.string().nullable(),
          name: z.string().nullable().optional(),
          selectedSource: z.string().nullable().optional(),
          isVisibleForUser: z.boolean().nullable().optional(),
          data: z
            .array(
              z
                .object({
                  labelName: z.string().nullable(),
                  valueOmnetic: z.string().nullable().optional(),
                  valueXentry: z.string().nullable().optional(),
                  valueType: z.string().nullable().optional(),
                  unit: z.string().nullable().optional(),
                })
                .nullable()
            )
            .optional(),
          groups: z
            .array(
              z
                .object({
                  type: z.string().nullable().optional(),
                  name: z.string().nullable().optional(),
                  selectedSource: z.string().nullable().optional(),
                  isVisibleForUser: z.boolean().nullable().optional(),
                  data: z
                    .array(
                      z
                        .object({
                          labelName: z.string().nullable(),
                          valueOmnetic: z.string().nullable().optional(),
                          valueXentry: z.string().nullable().optional(),
                          valueType: z.string().nullable().optional(),
                          unit: z.string().nullable().optional(),
                        })
                        .nullable()
                    )
                    .optional(),
                })
                .nullable()
            )
            .optional(),
        })
        .nullable()
    )
    .optional(),
});

export type ServiceCaseIntegrationsXentryOrderSynchronizationGroup = z.infer<
  typeof ServiceCaseIntegrationsXentryOrderSynchronizationGroupSchema
>;

export const PostServiceCaseIntegrationsXentryOrderSynchronizationApiResponseSchema = z
  .object({
    groups: z
      .array(
        z
          .object({
            type: z.enum([
              'checkin',
              'handover',
              'service-order',
              'service-campaign',
              'service-case-job',
            ]),
            name: z.string().nullable(),
            selectedSource: z.enum(['OMNETIC', 'XENTRY']),
            action: z.enum(['UPDATE', 'DELETE', 'CREATE']),
            isVisibleForUser: z.boolean().nullable(),
            omneticId: z.string().nullable(),
            omneticParentId: z.string().nullable(),
            xentryId: z.string().nullable(),
            xentryParentId: z.string().nullable(),
            eTag: z.string().nullable(),
            attributes: z.string().nullable(),
            comment: z.string().nullable(),
          })
          .nullable()
      )
      .optional(),
  })
  .nullable();

export type PostServiceCaseIntegrationsXentryOrderSynchronizationApiResponse = z.infer<
  typeof PostServiceCaseIntegrationsXentryOrderSynchronizationApiResponseSchema
>;

export const PostServiceCaseIntegrationsXentryOrderSynchronizationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  body: z
    .object({
      groups: z
        .array(
          z
            .object({
              type: z.enum([
                'checkin',
                'handover',
                'service-order',
                'service-campaign',
                'service-case-job',
              ]),
              name: z.string().nullable(),
              selectedSource: z.enum(['OMNETIC', 'XENTRY']),
              action: z.enum(['UPDATE', 'DELETE', 'CREATE']),
              isVisibleForUser: z.boolean().nullable(),
              omneticId: z.string().nullable(),
              omneticParentId: z.string().nullable(),
              xentryId: z.string().nullable(),
              xentryParentId: z.string().nullable(),
              eTag: z.string().nullable(),
              attributes: z.string().nullable(),
              comment: z.string().nullable(),
            })
            .nullable()
        )
        .optional(),
    })
    .nullish(),
});

export type PostServiceCaseIntegrationsXentryOrderSynchronizationApiArg = z.infer<
  typeof PostServiceCaseIntegrationsXentryOrderSynchronizationApiArgSchema
>;

const AudatexUserSettingsSchema = z.object({
  material: z.boolean().nullable(),
  labour: z.boolean().nullable(),
  paintWork: z.boolean().nullable(),
  paintPreparationWork: z.boolean().nullable(),
  paintMaterial: z.boolean().nullable(),
  paintConstantMaterial: z.boolean().nullable(),
  pcofPart: z.boolean().nullable(),
  discountsAndSurcharges: z.boolean().nullable(),
  otherCosts: z.boolean().nullable(),
});

export const GetAudatexCalculationsUserSettingsApiResponseSchema = z.union([
  z
    .object({
      settings: AudatexUserSettingsSchema.nullable(),
    })
    .nullable(),
  z.void(),
]);

export type GetAudatexCalculationsUserSettingsApiResponse = z.infer<
  typeof GetAudatexCalculationsUserSettingsApiResponseSchema
>;

export const GetAudatexCalculationsUserSettingsApiArgSchema = BaseVoidRequestSchema;

export type GetAudatexCalculationsUserSettingsApiArg = z.infer<
  typeof GetAudatexCalculationsUserSettingsApiArgSchema
>;

const MyClaimUserSettingsSchema = z.object({
  material: z.boolean().nullable(),
  labour: z.boolean().nullable(),
  lacquerWork: z.boolean().nullable(),
  lacquerMaterial: z.boolean().nullable(),
  lacquerConstant: z.boolean().nullable(),
  consumablesSurcharge: z.boolean().nullable(),
  dentWork: z.boolean().nullable(),
  discountsAndSurcharges: z.boolean().nullable(),
  otherCosts: z.boolean().nullable(),
});

export const GetMyClaimCalculationsUserSettingsApiResponseSchema = z.union([
  z
    .object({
      settings: MyClaimUserSettingsSchema.nullable(),
    })
    .nullable(),
  z.void(),
]);

export type GetMyClaimCalculationsUserSettingsApiResponse = z.infer<
  typeof GetMyClaimCalculationsUserSettingsApiResponseSchema
>;

export const GetMyClaimCalculationsUserSettingsApiArgSchema = BaseVoidRequestSchema;

export type GetMyClaimCalculationsUserSettingsApiArg = z.infer<
  typeof GetMyClaimCalculationsUserSettingsApiArgSchema
>;

export const GetServiceCaseIntegrationsClaraVehicleDetailsApiResponseSchema = z.union([
  z
    .object({
      vin: z.string().nullable().optional(),
      fin: z.string().nullable().optional(),
      mileage: z.number().nullable().optional(),
      mileageUnit: z.string().nullable().optional(),
      warrantyRegistrationDate: z.string().nullable().optional(),
      externalVehicleInformation: z.string().nullable().optional(),
      hasActiveServiceContract: z.boolean().nullable().optional(),
      hasActiveFieldMeasures: z.boolean().nullable().optional(),
    })
    .nullable(),
  z.void(),
]);

export type GetServiceCaseIntegrationsClaraVehicleDetailsApiResponse = z.infer<
  typeof GetServiceCaseIntegrationsClaraVehicleDetailsApiResponseSchema
>;

export const GetServiceCaseIntegrationsClaraVehicleDetailsApiArgSchema = z.object({
  serviceCaseId: z.string(),
});

export type GetServiceCaseIntegrationsClaraVehicleDetailsApiArg = z.infer<
  typeof GetServiceCaseIntegrationsClaraVehicleDetailsApiArgSchema
>;

export const ClaraClaimAttachmentSchema = z.object({
  fileId: z.string(),
  filename: z.string(),
  contentType: z.enum(['image/jpeg', 'image/png', 'image/gif', 'application/pdf']),
});

export type ClaraClaimAttachment = z.infer<typeof ClaraClaimAttachmentSchema>;

export const GetServiceCaseIntegrationsClaraClaimApiResponseSchema = z.object({
  claimType: z.string(),
  repairStartDate: z.string(),
  mileageStart: z.number().nullable(),
  repairEndDate: z.string(),
  mileageEnd: z.number().nullable(),
  partPurchaseDate: z.string().nullable(),
  mileagePartPurchase: z.number().nullable(),
  partNumber: z.string().nullable(),
  attachments: z.array(ClaraClaimAttachmentSchema).optional(),
});

export type GetServiceCaseIntegrationsClaraClaimApiResponse = z.infer<
  typeof GetServiceCaseIntegrationsClaraClaimApiResponseSchema
>;

export const GetServiceCaseIntegrationsClaraClaimApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceCaseIntegrationsClaraClaimApiArg = z.infer<
  typeof GetServiceCaseIntegrationsClaraClaimApiArgSchema
>;

export const GetServiceCaseIntegrationsClaraClaimDetailApiResponseSchema = z.object({
  claimNumber: z.string(),
  claimType: z.string(),
  status: z.enum([
    'PRE_APPROVAL',
    'SUBMITTED',
    'REOPENED',
    'APPEALED',
    'PENDING_PAYMENT',
    'DRAFT',
    'PENDING_APPROVAL',
    'PENDING_REVIEW',
    'RETURNED',
    'INFORMATION_NEEDED',
    'REJECTED',
    'NOT_CREATED',
    'APPROVED',
    'PAID',
    'CLOSED',
  ]),
  statusLevel: z.enum(['info', 'warning', 'error', 'success']).nullable(),
  repairStartDate: z.string(),
  mileageStart: z.number(),
  repairEndDate: z.string(),
  mileageEnd: z.number(),
  partPurchaseDate: z.string().nullable(),
  mileagePartPurchase: z.number().nullable(),
  partNumber: z.string().nullable(),
  issues: z
    .array(
      z.object({
        level: z.enum(['info', 'warning', 'error', 'success']).nullable(),
        content: z.string(),
      })
    )
    .optional(),
  attachments: z.array(ClaraClaimAttachmentSchema).optional(),
});

export type GetServiceCaseIntegrationsClaraClaimDetailApiResponse = z.infer<
  typeof GetServiceCaseIntegrationsClaraClaimDetailApiResponseSchema
>;

export const GetServiceCaseIntegrationsClaraClaimDetailApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceCaseIntegrationsClaraClaimDetailApiArg = z.infer<
  typeof GetServiceCaseIntegrationsClaraClaimDetailApiArgSchema
>;

export const PostServiceCaseIntegrationsClaraClaimApiResponseSchema = BaseVoidResponseSchema;
export type PostServiceCaseIntegrationsClaraClaimApiResponse = z.infer<
  typeof PostServiceCaseIntegrationsClaraClaimApiResponseSchema
>;

export const PostServiceCaseIntegrationsClaraClaimApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  body: z.object({
    claimType: z.string(),
    repairStartDate: z.string(),
    mileageStart: z.number(),
    repairEndDate: z.string(),
    mileageEnd: z.number(),
    partPurchaseDate: z.string().nullable().optional(),
    mileagePartPurchase: z.number().nullable().optional(),
    partNumber: z.string().nullable().optional(),
    attachments: z.array(ClaraClaimAttachmentSchema).optional(),
  }),
});

export type PostServiceCaseIntegrationsClaraClaimApiArg = z.infer<
  typeof PostServiceCaseIntegrationsClaraClaimApiArgSchema
>;

export const GetServiceOrderIntegrationClaraInformationApiResponseSchema = z.object({
  damageCode: z.string().max(7).nullable(),
});

export type GetServiceOrderIntegrationClaraInformationApiResponse = z.infer<
  typeof GetServiceOrderIntegrationClaraInformationApiResponseSchema
>;

export const GetServiceOrderIntegrationClaraInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceOrderIntegrationClaraInformationApiArg = z.infer<
  typeof GetServiceOrderIntegrationClaraInformationApiArgSchema
>;

export const PatchServiceOrderIntegrationClaraInformationApiResponseSchema = BaseVoidResponseSchema;
export type PatchServiceOrderIntegrationClaraInformationApiResponse = z.infer<
  typeof PatchServiceOrderIntegrationClaraInformationApiResponseSchema
>;

export const PatchServiceOrderIntegrationClaraInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  body: z.object({
    damageCode: z.string().max(7).nullish(),
  }),
});

export type PatchServiceOrderIntegrationClaraInformationApiArg = z.infer<
  typeof PatchServiceOrderIntegrationClaraInformationApiArgSchema
>;

export const GetServiceWorkItemIntegrationClaraInformationApiResponseSchema = z.object({
  otherCost: z.boolean(),
  costCategory: z
    .enum(['MISCELLANEOUS', 'MILEAGE_ALLOWANCE', 'TOWING', 'CHARGING_COSTS', 'FOREIGN_LABOR'])
    .nullable(),
  reasonForAdditionalLabour: z.string().nullable(),
});

export type GetServiceWorkItemIntegrationClaraInformationApiResponse = z.infer<
  typeof GetServiceWorkItemIntegrationClaraInformationApiResponseSchema
>;

export const GetServiceWorkItemIntegrationClaraInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  serviceJobId: z.string(),
  serviceItemId: z.string(),
});

export type GetServiceWorkItemIntegrationClaraInformationApiArg = z.infer<
  typeof GetServiceWorkItemIntegrationClaraInformationApiArgSchema
>;

export const PatchServiceWorkItemIntegrationClaraInformationApiResponseSchema =
  BaseVoidResponseSchema;
export type PatchServiceWorkItemIntegrationClaraInformationApiResponse = z.infer<
  typeof PatchServiceWorkItemIntegrationClaraInformationApiResponseSchema
>;

export const PatchServiceWorkItemIntegrationClaraInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  serviceJobId: z.string(),
  serviceItemId: z.string(),
  body: z.object({
    otherCost: z.boolean(),
    costCategory: z
      .enum(['MISCELLANEOUS', 'MILEAGE_ALLOWANCE', 'TOWING', 'CHARGING_COSTS', 'FOREIGN_LABOR'])
      .nullable(),
    reasonForAdditionalLabour: z.string().nullable(),
  }),
});

export type PatchServiceWorkItemIntegrationClaraInformationApiArg = z.infer<
  typeof PatchServiceWorkItemIntegrationClaraInformationApiArgSchema
>;

export const GetServiceMaterialItemIntegrationClaraInformationApiResponseSchema = z.object({
  oemPart: z.boolean(),
});

export type GetServiceMaterialItemIntegrationClaraInformationApiResponse = z.infer<
  typeof GetServiceMaterialItemIntegrationClaraInformationApiResponseSchema
>;

export const GetServiceMaterialItemIntegrationClaraInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  serviceJobId: z.string(),
  serviceItemId: z.string(),
});

export type GetServiceMaterialItemIntegrationClaraInformationApiArg = z.infer<
  typeof GetServiceMaterialItemIntegrationClaraInformationApiArgSchema
>;

export const PatchServiceMaterialItemIntegrationClaraInformationApiResponseSchema =
  BaseVoidResponseSchema;
export type PatchServiceMaterialItemIntegrationClaraInformationApiResponse = z.infer<
  typeof PatchServiceMaterialItemIntegrationClaraInformationApiResponseSchema
>;

export const PatchServiceMaterialItemIntegrationClaraInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  serviceJobId: z.string(),
  serviceItemId: z.string(),
  body: z.object({
    oemPart: z.boolean(),
  }),
});

export type PatchServiceMaterialItemIntegrationClaraInformationApiArg = z.infer<
  typeof PatchServiceMaterialItemIntegrationClaraInformationApiArgSchema
>;

export const XentryPositionTypeSchema = z.enum(['CUSTOMER_REQUEST', 'MAINTENANCE', 'COMPLAINT']);

export type XentryPositionType = z.infer<typeof XentryPositionTypeSchema>;

export const GetServiceJobIntegrationXentryInformationApiResponseSchema = z.object({
  damageCode: z.string().nullable(),
  eskulabDecision: z
    .object({
      authorizationNumber: z.number().nullable(),
      statusCode: z.string().nullable(),
      validityDate: z.string().nullable(),
      validityMileage: z.number().nullable(),
    })
    .nullable(),
  groundedVehicle: z
    .object({
      isEditable: z.boolean().nullable(),
      tooltip: z.string().nullable(),
      isGrounded: z.boolean().nullable(),
      groundingReason: z.string().nullable(),
    })
    .nullable(),
  xentryPositionType: XentryPositionTypeSchema.nullish(),
});

export type GetServiceJobIntegrationXentryInformationApiResponse = z.infer<
  typeof GetServiceJobIntegrationXentryInformationApiResponseSchema
>;

export const GetServiceJobIntegrationXentryInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  serviceJobId: z.string(),
});

export type GetServiceJobIntegrationXentryInformationApiArg = z.infer<
  typeof GetServiceJobIntegrationXentryInformationApiArgSchema
>;

export const PatchServiceJobIntegrationXentryInformationApiResponseSchema = BaseVoidResponseSchema;
export type PatchServiceJobIntegrationXentryInformationApiResponse = z.infer<
  typeof PatchServiceJobIntegrationXentryInformationApiResponseSchema
>;

export const PatchServiceJobIntegrationXentryInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  serviceJobId: z.string(),
  body: z.object({
    damageCode: z.string().max(9).nullable(),
    eskulabDecision: z
      .object({
        authorizationNumber: z.number().nullable(),
        statusCode: z.string().nullable(),
        validityDate: z.string().nullable(),
        validityMileage: z.number().nullable(),
      })
      .nullable(),
    groundedVehicle: z
      .object({
        isGrounded: z.boolean().nullable(),
        groundingReason: z.string().nullable(),
      })
      .nullable(),
    xentryPositionType: XentryPositionTypeSchema.nullable(),
  }),
});

export type PatchServiceJobIntegrationXentryInformationApiArg = z.infer<
  typeof PatchServiceJobIntegrationXentryInformationApiArgSchema
>;

export const GetIntegrationCustomerApiResponseSchema = z.string().nullish();

export type GetIntegrationCustomerApiResponse = z.infer<
  typeof GetIntegrationCustomerApiResponseSchema
>;

export const GetIntegrationCustomerApiArgSchema = z.object({
  customerId: z.string(),
});

export type GetIntegrationCustomerApiArg = z.infer<typeof GetIntegrationCustomerApiArgSchema>;

export const GetServiceCaseIntegrationsXentrySynchronizationStatusApiResponseSchema = z.object({
  integrationStatus: z.enum(['LINKED', 'UNLINKED', 'PENDING']),
  pendingReason: z.enum(['MISSING_VIN', 'INVALID_VIN']).nullable(),
  lastUpdateFromXentryAt: z.string().nullable(),
  synchronizeAfterOpening: z.boolean().nullable(),
  xentryJobData: z
    .object({
      externalId: z.string().nullable(),
      viewDetailsLink: z.string().nullable(),
    })
    .nullable(),
  actions: z.array(ServiceCaseIntegrationActionSchema).nullable(),
});

export type GetServiceCaseIntegrationsXentrySynchronizationStatusApiResponse = z.infer<
  typeof GetServiceCaseIntegrationsXentrySynchronizationStatusApiResponseSchema
>;

export const GetServiceCaseIntegrationsXentrySynchronizationStatusApiArgSchema = z.object({
  serviceCaseId: z.string(),
});

export type GetServiceCaseIntegrationsXentrySynchronizationStatusApiArg = z.infer<
  typeof GetServiceCaseIntegrationsXentrySynchronizationStatusApiArgSchema
>;

export const GetServiceCaseIntegrationsEvaClaimTooltipSchema = z.object({
  claimType: z.number(),
  damageCode: z.string(),
  eskulabAuthorizationNumber: z.string().nullish(),
  serviceRequests: z
    .array(
      z.object({
        title: z.string(),
      })
    )
    .nullish(),
});

export type GetServiceCaseIntegrationsEvaClaimTooltip = z.infer<
  typeof GetServiceCaseIntegrationsEvaClaimTooltipSchema
>;

export const GetServiceCaseIntegrationsEvaClaimServiceOrderSchema = z.object({
  serviceOrderId: z.string(),
  number: z.string(),
  variantName: z.string().nullish(),
  isSelected: z.boolean(),
  tooltip: GetServiceCaseIntegrationsEvaClaimTooltipSchema.nullish(),
});

export type GetServiceCaseIntegrationsEvaClaimServiceOrder = z.infer<
  typeof GetServiceCaseIntegrationsEvaClaimServiceOrderSchema
>;

export const GetServiceCaseIntegrationsEvaClaimDetailApiResponseSchema = z.object({
  dealerClaimNumber: z.string().nullish(),
  dealerClaimNumberTooltip: z.string().nullish(),
  repairStartDate: z.string().nullish(),
  mileageStart: z.number().nullish(),
  repairEndDate: z.string().nullish(),
  mileageEnd: z.number().nullish(),
  claimText: z.string().nullish(),
  orders: z.array(GetServiceCaseIntegrationsEvaClaimServiceOrderSchema),
});

export type GetServiceCaseIntegrationsEvaClaimDetailApiResponse = z.infer<
  typeof GetServiceCaseIntegrationsEvaClaimDetailApiResponseSchema
>;

export const GetServiceCaseIntegrationsEvaClaimDetailApiArgSchema = z.object({
  serviceCaseId: z.string(),
});

export type GetServiceCaseIntegrationsEvaClaimDetailApiArg = z.infer<
  typeof GetServiceCaseIntegrationsEvaClaimDetailApiArgSchema
>;

export const PostServiceCaseIntegrationsEvaClaimExportApiArgSchema = z.object({
  serviceCaseId: z.string(),
  body: z.object({
    dealerClaimNumber: z.string(),
    repairStartDate: z.string(),
    mileageStart: z.number(),
    repairEndDate: z.string().nullable(),
    mileageEnd: z.number().nullable(),
    claimText: z.string().nullable(),
    orders: z.array(
      z.object({
        serviceOrderId: z.string(),
        isSelected: z.boolean(),
      })
    ),
  }),
});

export type PostServiceCaseIntegrationsEvaClaimExportApiArg = z.infer<
  typeof PostServiceCaseIntegrationsEvaClaimExportApiArgSchema
>;

export const PostServiceCaseIntegrationsEvaClaimExportApiResponseSchema = z.object({
  filename: z.string(),
  link: z.string(),
});

export type PostServiceCaseIntegrationsEvaClaimExportApiResponse = z.infer<
  typeof PostServiceCaseIntegrationsEvaClaimExportApiResponseSchema
>;

export const GetServiceCaseIntegrationsEvaClaimAdditionalInformationApiResponseSchema = z.object({
  damageCode: z.string().max(7).nullish(),
  isPartClaimDamage: z.boolean().nullish(),
  partClaimData: z
    .object({
      installationDate: z.date().nullable(),
      installationMileage: z.number().nullable(),
      replacedByDealer: z.string().nullable(),
      replacedPartNumber: z.string().nullable(),
    })
    .nullable(),
});

export type GetServiceCaseIntegrationsEvaClaimAdditionalInformationApiResponse = z.infer<
  typeof GetServiceCaseIntegrationsEvaClaimAdditionalInformationApiResponseSchema
>;

export const GetServiceCaseIntegrationsEvaClaimAdditionalInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceCaseIntegrationsEvaClaimAdditionalInformationApiArg = z.infer<
  typeof GetServiceCaseIntegrationsEvaClaimAdditionalInformationApiArgSchema
>;

export const PatchServiceCaseIntegrationsEvaClaimAdditionalInformationApiResponseSchema =
  BaseVoidResponseSchema;
export type PatchServiceCaseIntegrationsEvaClaimAdditionalInformationApiResponse = z.infer<
  typeof PatchServiceCaseIntegrationsEvaClaimAdditionalInformationApiResponseSchema
>;

export const PatchServiceCaseIntegrationsEvaClaimAdditionalInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  body: z.object({
    damageCode: z.string().max(7).nullish(),
    isPartClaimDamage: z.boolean().nullish(),
    partClaimData: z
      .object({
        installationDate: z.date().nullish(),
        installationMileage: z.number().nullish(),
        replacedByDealer: z.string().nullish(),
        replacedPartNumber: z.string().nullish(),
      })
      .nullish(),
  }),
});

export type PatchServiceCaseIntegrationsEvaClaimAdditionalInformationApiArg = z.infer<
  typeof PatchServiceCaseIntegrationsEvaClaimAdditionalInformationApiArgSchema
>;

export const GetServiceOrderIntegrationsMazdaAdditionalInformationApiResponseSchema = z
  .object({
    assemblyGroup: z.string().nullish(),
    subAssemblyGroup: z.string().nullish(),
    pncCode: z.string().nullish(),
    symptomCode: z.string().nullish(),
    damageCode: z.string().nullish(),
    locationCodes: z.array(z.string()).nullish(),
    dtcCodes: z.array(z.string()).nullish(),
    causalPartNumber: z.string().nullish(),
    isEditable: z.boolean().nullish(),
  })
  .nullish();

export type GetServiceOrderIntegrationsMazdaAdditionalInformationApiResponse = z.infer<
  typeof GetServiceOrderIntegrationsMazdaAdditionalInformationApiResponseSchema
>;

export const GetServiceOrderIntegrationsMazdaAdditionalInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceOrderIntegrationsMazdaAdditionalInformationApiArg = z.infer<
  typeof GetServiceOrderIntegrationsMazdaAdditionalInformationApiArgSchema
>;

export const PatchServiceOrderIntegrationsMazdaAdditionalInformationApiResponseSchema =
  BaseVoidResponseSchema;
export type PatchServiceOrderIntegrationsMazdaAdditionalInformationApiResponse = z.infer<
  typeof PatchServiceOrderIntegrationsMazdaAdditionalInformationApiResponseSchema
>;

export const PatchServiceOrderIntegrationsMazdaAdditionalInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  body: z.object({
    assemblyGroup: z.string().nullish(),
    subAssemblyGroup: z.string().nullish(),
    pncCode: z.string().nullish(),
    symptomCode: z.string().nullish(),
    damageCode: z.string().nullish(),
    locationCodes: z.array(z.string()).nullish(),
    dtcCodes: z.array(z.string()).nullish(),
    causalPartNumber: z.string().nullish(),
  }),
});

export type PatchServiceOrderIntegrationsMazdaAdditionalInformationApiArg = z.infer<
  typeof PatchServiceOrderIntegrationsMazdaAdditionalInformationApiArgSchema
>;

export const GetServiceWorkItemIntegrationsMazdaAdditionalInformationApiResponseSchema = z
  .object({
    repairDescription: z.string().nullish(),
    invoiceNumber: z.string().nullish(),
    invoiceDate: z.string().nullish(),
    invoiceCompanyName: z.string().nullish(),
  })
  .nullish();

export type GetServiceWorkItemIntegrationsMazdaAdditionalInformationApiResponse = z.infer<
  typeof GetServiceWorkItemIntegrationsMazdaAdditionalInformationApiResponseSchema
>;

export const GetServiceWorkItemIntegrationsMazdaAdditionalInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  serviceJobId: z.string(),
  serviceItemId: z.string(),
});

export type GetServiceWorkItemIntegrationsMazdaAdditionalInformationApiArg = z.infer<
  typeof GetServiceWorkItemIntegrationsMazdaAdditionalInformationApiArgSchema
>;

export const PatchServiceWorkItemIntegrationsMazdaAdditionalInformationApiResponseSchema =
  BaseVoidResponseSchema;
export type PatchServiceWorkItemIntegrationsMazdaAdditionalInformationApiResponse = z.infer<
  typeof PatchServiceWorkItemIntegrationsMazdaAdditionalInformationApiResponseSchema
>;

export const PatchServiceWorkItemIntegrationsMazdaAdditionalInformationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  serviceJobId: z.string(),
  serviceItemId: z.string(),
  body: z.object({
    repairDescription: z.string().nullish(),
    invoiceNumber: z.string().nullish(),
    invoiceDate: z.string().nullish(),
    invoiceCompanyName: z.string().nullish(),
  }),
});

export type PatchServiceWorkItemIntegrationsMazdaAdditionalInformationApiArg = z.infer<
  typeof PatchServiceWorkItemIntegrationsMazdaAdditionalInformationApiArgSchema
>;

export const MazdaAssessmentCodeSchema = z.object({
  code: z.string().nullish(),
  description: z.string().nullish(),
  additionalInformation: z.string().nullish(),
});

export type MazdaAssessmentCode = z.infer<typeof MazdaAssessmentCodeSchema>;

export const MazdaDecisionOverallSchema = z.object({
  totalRequestedAmount: z.number().nullish(),
  totalAgreedAmount: z.number().nullish(),
  totalRequestedAmountParts: z.number().nullish(),
  totalAgreedAmountParts: z.number().nullish(),
  totalRequestedAmountLabour: z.number().nullish(),
  totalAgreedAmountLabour: z.number().nullish(),
  totalRequestedAmountSublets: z.number().nullish(),
  totalAgreedAmountSublets: z.number().nullish(),
});

export type MazdaDecisionOverall = z.infer<typeof MazdaDecisionOverallSchema>;

export const MazdaDecisionItemSchema = z.object({
  type: z.string().nullish(),
  catalogNumber: z.string().nullish(),
  sequenceNumber: z.number().nullish(),
  requestedQuantity: z.number().nullish(),
  agreedQuantity: z.number().nullish(),
  requestedAmount: z.number().nullish(),
  agreedAmount: z.number().nullish(),
});

export type MazdaDecisionItem = z.infer<typeof MazdaDecisionItemSchema>;

export const MazdaDecisionSchema = z.object({
  overall: MazdaDecisionOverallSchema.nullish(),
  items: z.array(MazdaDecisionItemSchema).nullish(),
});

export type MazdaDecision = z.infer<typeof MazdaDecisionSchema>;

export const MazdaPreauthorizationExternalStatusSchema = z.enum([
  'NEW',
  'RETURNED',
  'PENDING',
  'SUSPENDED',
  'CANCELLED',
  'CANCELED',
  'ACCEPTED',
  'DENIED',
]);

export type MazdaPreauthorizationExternalStatus = z.infer<
  typeof MazdaPreauthorizationExternalStatusSchema
>;

export const GetServiceOrderIntegrationsMazdaPreauthorizationDetailApiResponseSchema = z
  .object({
    cached: z.boolean().nullish(),
    externalId: z.string().nullish(),
    externalStatus: MazdaPreauthorizationExternalStatusSchema.nullish(),
    externalStatusDescription: z.string().nullish(),
    nextActor: z.string().nullish(),
    scope: z.string().nullish(),
    warrantyType: z.string().nullish(),
    mileage: z.number().nullish(),
    vehicleServicedAccordingToManufacturerSpecs: z.boolean().nullish(),
    repairDate: z.string().nullish(),
    installationDate: z.string().nullish(),
    installationMileage: z.number().nullish(),
    installationRepairNo: z.string().nullish(),
    causalPart: z.string().nullish(),
    specialCausalPart: z.string().nullish(),
    message: z.string().nullish(),
    assessmentCodes: z.array(MazdaAssessmentCodeSchema).nullish(),
    decision: MazdaDecisionSchema.nullish(),
  })
  .nullish();

export type GetServiceOrderIntegrationsMazdaPreauthorizationDetailApiResponse = z.infer<
  typeof GetServiceOrderIntegrationsMazdaPreauthorizationDetailApiResponseSchema
>;

export const GetServiceOrderIntegrationsMazdaPreauthorizationDetailApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceOrderIntegrationsMazdaPreauthorizationDetailApiArg = z.infer<
  typeof GetServiceOrderIntegrationsMazdaPreauthorizationDetailApiArgSchema
>;

export const MazdaPreauthorizationScopeSchema = z.enum(['warranty', 'goodwill']);

export type MazdaPreauthorizationScope = z.infer<typeof MazdaPreauthorizationScopeSchema>;

export const MazdaPreauthorizationWarrantyTypeSchema = z.enum([
  'basic_warranty',
  'predelivery_inspection',
  'part_warranty',
  'repeated_repair',
]);

export type MazdaPreauthorizationWarrantyType = z.infer<
  typeof MazdaPreauthorizationWarrantyTypeSchema
>;

export const GetServiceOrderIntegrationsMazdaPreauthorizationApiResponseSchema = z
  .object({
    externalId: z.string().nullish(),
    externalStatus: z.string().nullish(),
    scope: z.string().nullish(),
    warrantyType: z.string().nullish(),
    mileage: z.number().nullish(),
    vehicleServicedAccordingToManufacturerSpecs: z.boolean().nullish(),
    installationDate: z.string().nullish(),
    installationMileage: z.number().nullish(),
    installationRepairNo: z.string().nullish(),
    causalPart: z.string().nullish(),
    specialCausalPart: z.string().nullish(),
    message: z.string().nullish(),
    assessmentCodes: z.array(MazdaAssessmentCodeSchema).nullish(),
    decision: MazdaDecisionSchema.nullish(),
  })
  .nullish();

export type GetServiceOrderIntegrationsMazdaPreauthorizationApiResponse = z.infer<
  typeof GetServiceOrderIntegrationsMazdaPreauthorizationApiResponseSchema
>;

export const GetServiceOrderIntegrationsMazdaPreauthorizationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceOrderIntegrationsMazdaPreauthorizationApiArg = z.infer<
  typeof GetServiceOrderIntegrationsMazdaPreauthorizationApiArgSchema
>;

export const PutServiceOrderIntegrationsMazdaPreauthorizationApiResponseSchema =
  BaseVoidResponseSchema;
export type PutServiceOrderIntegrationsMazdaPreauthorizationApiResponse = z.infer<
  typeof PutServiceOrderIntegrationsMazdaPreauthorizationApiResponseSchema
>;

export const PutServiceOrderIntegrationsMazdaPreauthorizationApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  body: z.object({
    scope: MazdaPreauthorizationScopeSchema.nullish(),
    warrantyType: MazdaPreauthorizationWarrantyTypeSchema.nullish(),
    mileage: z.number().nullish(),
    vehicleServicedAccordingToManufacturerSpecs: z.boolean().nullish(),
    installationDate: z.string().nullish(),
    installationMileage: z.number().nullish(),
    installationRepairNo: z.string().nullish(),
    causalPart: z.string().nullish(),
    specialCausalPart: z.string().nullish(),
    message: z.string().nullish(),
  }),
});

export type PutServiceOrderIntegrationsMazdaPreauthorizationApiArg = z.infer<
  typeof PutServiceOrderIntegrationsMazdaPreauthorizationApiArgSchema
>;

export const PostServiceOrderIntegrationsMazdaPreauthorizationSendApiResponseSchema =
  BaseVoidResponseSchema;
export type PostServiceOrderIntegrationsMazdaPreauthorizationSendApiResponse = z.infer<
  typeof PostServiceOrderIntegrationsMazdaPreauthorizationSendApiResponseSchema
>;

export const PostServiceOrderIntegrationsMazdaPreauthorizationSendApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type PostServiceOrderIntegrationsMazdaPreauthorizationSendApiArg = z.infer<
  typeof PostServiceOrderIntegrationsMazdaPreauthorizationSendApiArgSchema
>;

export const MazdaClaimScopeSchema = z.enum(['warranty', 'goodwill']);

export type MazdaClaimScope = z.infer<typeof MazdaClaimScopeSchema>;

export const MazdaClaimWarrantyTypeSchema = z.enum([
  'basic_warranty',
  'predelivery_inspection',
  'part_warranty',
  'repeated_repair',
]);

export type MazdaClaimWarrantyType = z.infer<typeof MazdaClaimWarrantyTypeSchema>;

export const MazdaClaimMessageSchema = z.object({
  code: z.string().nullish(),
  description: z.string().nullish(),
});

export type MazdaClaimMessage = z.infer<typeof MazdaClaimMessageSchema>;

export const GetServiceOrderIntegrationsMazdaClaimApiResponseSchema = z
  .object({
    externalId: z.string().nullish(),
    externalStatus: z.string().nullish(),
    scope: z.string().nullish(),
    warrantyType: z.string().nullish(),
    mileage: z.number().nullish(),
    repairDate: z.string().nullish(),
    vehicleServicedAccordingToManufacturerSpecs: z.boolean().nullish(),
    installationDate: z.string().nullish(),
    installationMileage: z.number().nullish(),
    installationRepairNo: z.string().nullish(),
    causalPart: z.string().nullish(),
    specialCausalPart: z.string().nullish(),
    message: z.string().nullish(),
    campaignNumber: z.string().nullish(),
    campaignVariantNumber: z.number().nullish(),
  })
  .nullish();

export type GetServiceOrderIntegrationsMazdaClaimApiResponse = z.infer<
  typeof GetServiceOrderIntegrationsMazdaClaimApiResponseSchema
>;

export const GetServiceOrderIntegrationsMazdaClaimApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceOrderIntegrationsMazdaClaimApiArg = z.infer<
  typeof GetServiceOrderIntegrationsMazdaClaimApiArgSchema
>;

export const PutServiceOrderIntegrationsMazdaClaimApiResponseSchema = BaseVoidResponseSchema;

export type PutServiceOrderIntegrationsMazdaClaimApiResponse = z.infer<
  typeof PutServiceOrderIntegrationsMazdaClaimApiResponseSchema
>;

export const PutServiceOrderIntegrationsMazdaClaimApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  body: z.object({
    scope: MazdaClaimScopeSchema.nullish(),
    warrantyType: MazdaClaimWarrantyTypeSchema.nullish(),
    mileage: z.number().nullish(),
    repairDate: z.string().nullish(),
    vehicleServicedAccordingToManufacturerSpecs: z.boolean().nullish(),
    installationDate: z.string().nullish(),
    installationMileage: z.number().nullish(),
    installationRepairNo: z.string().nullish(),
    causalPart: z.string().nullish(),
    specialCausalPart: z.string().nullish(),
    message: z.string().nullish(),
  }),
});

export type PutServiceOrderIntegrationsMazdaClaimApiArg = z.infer<
  typeof PutServiceOrderIntegrationsMazdaClaimApiArgSchema
>;

export const PostServiceOrderIntegrationsMazdaClaimSendApiResponseSchema = z
  .object({
    cached: z.boolean().nullish(),
    externalId: z.string().nullish(),
    externalStatus: z.string().nullish(),
    externalStatusDescription: z.string().nullish(),
    nextActor: z.string().nullish(),
    scope: z.string().nullish(),
    campaignNumber: z.string().nullish(),
    campaignVariantNumber: z.number().nullish(),
    warrantyType: z.string().nullish(),
    mileage: z.number().nullish(),
    vehicleServicedAccordingToManufacturerSpecs: z.boolean().nullish(),
    repairDate: z.string().nullish(),
    installationDate: z.string().nullish(),
    installationMileage: z.number().nullish(),
    installationRepairNo: z.string().nullish(),
    causalPart: z.string().nullish(),
    specialCausalPart: z.string().nullish(),
    message: z.string().nullish(),
    assessmentCodes: z.array(MazdaAssessmentCodeSchema).nullish(),
    warnings: z.array(MazdaClaimMessageSchema).nullish(),
    errors: z.array(MazdaClaimMessageSchema).nullish(),
    decision: MazdaDecisionSchema.nullish(),
  })
  .nullish();

export type PostServiceOrderIntegrationsMazdaClaimSendApiResponse = z.infer<
  typeof PostServiceOrderIntegrationsMazdaClaimSendApiResponseSchema
>;

export const PostServiceOrderIntegrationsMazdaClaimSendApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type PostServiceOrderIntegrationsMazdaClaimSendApiArg = z.infer<
  typeof PostServiceOrderIntegrationsMazdaClaimSendApiArgSchema
>;

export const GetServiceOrderIntegrationsMazdaClaimDetailApiResponseSchema = z
  .object({
    cached: z.boolean().nullish(),
    externalId: z.string().nullish(),
    externalStatus: z.string().nullish(),
    externalStatusDescription: z.string().nullish(),
    nextActor: z.string().nullish(),
    campaignNumber: z.string().nullish(),
    campaignVariantNumber: z.number().nullish(),
    scope: z.string().nullish(),
    warrantyType: z.string().nullish(),
    mileage: z.number().nullish(),
    vehicleServicedAccordingToManufacturerSpecs: z.boolean().nullish(),
    repairDate: z.string().nullish(),
    installationDate: z.string().nullish(),
    installationMileage: z.number().nullish(),
    installationRepairNo: z.string().nullish(),
    causalPart: z.string().nullish(),
    specialCausalPart: z.string().nullish(),
    message: z.string().nullish(),
    assessmentCodes: z.array(MazdaAssessmentCodeSchema).nullish(),
    decision: MazdaDecisionSchema.nullish(),
  })
  .nullish();

export type GetServiceOrderIntegrationsMazdaClaimDetailApiResponse = z.infer<
  typeof GetServiceOrderIntegrationsMazdaClaimDetailApiResponseSchema
>;

export const GetServiceOrderIntegrationsMazdaClaimDetailApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceOrderIntegrationsMazdaClaimDetailApiArg = z.infer<
  typeof GetServiceOrderIntegrationsMazdaClaimDetailApiArgSchema
>;

export const VehicleServiceChecklistDefinitionSchema = z.object({
  id: z.number().nullish(),
  name: z.string().nullish(),
  description: z.string().nullish(),
});

export type VehicleServiceChecklistDefinition = z.infer<
  typeof VehicleServiceChecklistDefinitionSchema
>;

export const GetVehicleServiceChecklistsApiResponseSchema = z
  .object({
    checklistDefinition: z.array(VehicleServiceChecklistDefinitionSchema).nullish(),
    count: z.number().nullish(),
  })
  .nullish();

export type GetVehicleServiceChecklistsApiResponse = z.infer<
  typeof GetVehicleServiceChecklistsApiResponseSchema
>;

export const GetVehicleServiceChecklistsApiArgSchema = z.object({
  vehicleId: z.string(),
});

export type GetVehicleServiceChecklistsApiArg = z.infer<
  typeof GetVehicleServiceChecklistsApiArgSchema
>;

export const PostVehicleServiceChecklistApiResponseSchema = z
  .object({
    id: z.string().nullish(),
  })
  .nullish();

export type PostVehicleServiceChecklistApiResponse = z.infer<
  typeof PostVehicleServiceChecklistApiResponseSchema
>;

export const PostVehicleServiceChecklistApiArgSchema = z.object({
  vehicleId: z.string(),
  // Not sent to the backend — carried only so the mutation can invalidate case-level caches.
  serviceCaseId: z.string(),
  body: z.object({
    serviceOrderId: z.string(),
    checklistDefinitionId: z.number(),
  }),
});

export type PostVehicleServiceChecklistApiArg = z.infer<
  typeof PostVehicleServiceChecklistApiArgSchema
>;
